import { Button, ButtonGroup, ModalProps } from '@chakra-ui/react';
import { DataTable } from '@gamma/ui/data-table';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';
import { Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Column as TableColumn } from 'react-table';
import {
  useDateRangeQueryString,
  useManualPagination,
} from '../../../../shared/hooks';
import { i18n } from '../../../../shared/localization';
import { Detection, NicHistory } from '../../../../shared/generated/graphql';
import { NICHistory } from '../../../entities/queries/types';
import { useGetNicHistoryForEndpointDeviceEntity } from '../../../entities/queries';

export type InterfaceHistoryModalProps = Partial<ModalProps> & {
  edrEntityId: string;
  detectionInfo: Detection;
};
const { entityEDR } = i18n.pages.detections;
const { headings } = entityEDR;
export const InterfaceHistoryModal = ({
  isOpen,
  onClose,
  detectionInfo,
  edrEntityId,
}: InterfaceHistoryModalProps) => {
  const { latest_start_timestamp, tenant } = detectionInfo || {};

  const dataTableInterfaceColumns: TableColumn<NICHistory>[] = [
    {
      Header: entityEDR.tableHeaders.ipAddress,
      accessor: 'ip_address',
      disableSortBy: true,
    },
    {
      Header: entityEDR.macAddress,
      accessor: 'mac_address',
      disableSortBy: true,
    },
    {
      Header: entityEDR.timestamp,
      accessor: 'obsts',
      Cell: (props: { value: number }) => {
        const value = props.value ?? 0;
        return <>{moment.unix(value).format('MMMM Do, h:mma')}</>;
      },
    },
  ];

  const { start, end } = useDateRangeQueryString();

  useEffect(() => {
    nicSetInitialFetch(true);
  }, [start, end]);

  const [nicInitialFetch, nicSetInitialFetch] = useState<boolean>(false);

  const {
    sortBy: nicSortBy,
    offset: nicOffset,
    pageSize: nicPageSize,
    pageIndex: nicPageIndex,
    pageCount: nicPageCount,
    onFetchData: nicOnFetchData,
    getPageCount: nicGetPageCount,
    getSortsForAPI: nicGetSortsForAPI,
  } = useManualPagination<NICHistory>({
    initialFetch: nicInitialFetch,
    sortBy: [{ id: 'obsts', desc: true }],
  });

  const {
    loading: getNicHistoryLoading,
    data: getNicHistoryData,
    error: getNicHistoryError,
  } = useGetNicHistoryForEndpointDeviceEntity({
    skip: !isOpen || !edrEntityId,
    variables: {
      as_of: latest_start_timestamp,
      entity: {
        entity_id: edrEntityId,
        entity_type: 'endpoint_device',
      },
      tenant,
      offset: nicOffset,
      size: nicPageSize,
      sort: nicGetSortsForAPI(nicSortBy),
    },
    onCompleted: ({ getNicHistoryForEndpointDeviceEntity }) => {
      nicSetInitialFetch(false);
      nicGetPageCount(getNicHistoryForEndpointDeviceEntity);
    },
  });

  // Map NicHistory (GraphQL) to NICHistory (local interface)
  const mapNicHistoryToNICHistory = (nic: NicHistory): NICHistory => ({
    ip_address: nic.ip_address ?? '',
    mac_address: nic.mac_address ?? '',
    obsts: nic.obsts ?? 0,
    data_source: nic.data_source ?? '',
    tenant: nic.tenant ?? '',
  });

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      title={headings.interfaceHistory}
      body={
        getNicHistoryLoading || getNicHistoryError ? (
          <GraphQLReqStatus
            error={getNicHistoryError}
            loading={getNicHistoryLoading}
          />
        ) : (
          <Panel>
            <FlushPanelContent>
              <DataTable
                isLined
                pageSize={nicPageSize}
                pageCount={nicPageCount}
                autoResetPage={false}
                columns={dataTableInterfaceColumns}
                autoResetSortBy={false}
                isPaginationManual={true}
                onFetchData={nicOnFetchData}
                itemCount={
                  getNicHistoryData?.getNicHistoryForEndpointDeviceEntity
                    ?.total_items || 0
                }
                initialState={{
                  pageIndex: nicPageIndex,
                  sortBy: nicSortBy,
                }}
                data={
                  getNicHistoryData?.getNicHistoryForEndpointDeviceEntity
                    ?.nic_histories
                    ? getNicHistoryData.getNicHistoryForEndpointDeviceEntity.nic_histories.map(
                        mapNicHistoryToNICHistory,
                      )
                    : []
                }
                isPaginable={Boolean(
                  !getNicHistoryLoading &&
                    !getNicHistoryError &&
                    getNicHistoryData?.getNicHistoryForEndpointDeviceEntity
                      ?.nic_histories?.length,
                )}
              />
            </FlushPanelContent>
          </Panel>
        )
      }
      footer={
        <ButtonGroup>
          <Button variant="solid" colorScheme="gray" onClick={onClose}>
            {entityEDR.close}
          </Button>
        </ButtonGroup>
      }
    />
  );
};
